import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMount } from 'react-use';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@eluve/components';
import {
  AudioDeviceOption,
  MICROPHONE_DEVICES_CHANGED_EVENT,
  getMicrophoneDevices,
} from '@eluve/mic-utils';
import { useAnalytics } from '@eluve/posthog-react';

interface MicrophoneDeviceSelectorProps {
  getDevices?: () => Promise<AudioDeviceOption[]>;
  onSelectedDeviceChange: (device: AudioDeviceOption | null) => void;
  selectedId: string | null;
  usesPortal?: boolean;
  preferredMicrophoneNames?: string[];
  side?: 'top' | 'bottom' | 'left' | 'right';
  hasError?: boolean;
}

export const MicrophoneDeviceSelector: FC<MicrophoneDeviceSelectorProps> = ({
  getDevices = getMicrophoneDevices,
  onSelectedDeviceChange,
  selectedId,
  usesPortal,
  preferredMicrophoneNames,
  side,
  hasError,
}) => {
  const isLoading = useRef(false);
  const [options, setOptions] = useState<AudioDeviceOption[]>([]);
  const analytics = useAnalytics('microphone-device-selector');

  const selectedMicrophone = useMemo(
    () => options.find((option) => option.deviceId === selectedId),
    [options, selectedId],
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      analytics.capture('appointment.audio_source_dropdown_clicked', {
        audio_source_count: options.length,
      });
    }
  };

  const handleValueChange = (deviceId: string) => {
    const device = options.find((option) => option.deviceId === deviceId);
    analytics.capture('appointment.audio_source_selected', {
      audio_source: device?.label ?? deviceId,
    });
    onSelectedDeviceChange(device ?? null);
  };

  const fetchOptions = useCallback(async () => {
    if (isLoading.current) {
      return;
    }
    isLoading.current = true;
    const devices = await getDevices();
    setOptions(devices);
    isLoading.current = false;
  }, [getDevices]);

  useMount(() => fetchOptions());

  useEffect(() => {
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', fetchOptions);
    }

    window.addEventListener(MICROPHONE_DEVICES_CHANGED_EVENT, fetchOptions);

    return () => {
      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          fetchOptions,
        );
      }
      window.removeEventListener(
        MICROPHONE_DEVICES_CHANGED_EVENT,
        fetchOptions,
      );
    };
  }, [fetchOptions]);

  useEffect(() => {
    if (isLoading.current) {
      return;
    }

    const canSelectDefault = !selectedId && options.length > 0;
    const shouldClearOldId = selectedId && !selectedMicrophone;

    if (canSelectDefault || shouldClearOldId) {
      let selectedDevice = null;

      // 1. Try to find preferred device by name (if configured)
      // Loop through preferred names in order and find first match
      if (preferredMicrophoneNames && preferredMicrophoneNames.length > 0) {
        for (const preferredName of preferredMicrophoneNames) {
          selectedDevice = options.find((device) =>
            device.label.toLowerCase().includes(preferredName.toLowerCase()),
          );
          if (selectedDevice) {
            break;
          }
        }
      }

      // 2. Fall back to "default" device
      selectedDevice ??= options.find(
        (device) => device.deviceId === 'default',
      );

      // 3. Fall back to first available
      selectedDevice ??= options[0];

      onSelectedDeviceChange(selectedDevice ?? null);
    }
  }, [
    onSelectedDeviceChange,
    options,
    selectedId,
    selectedMicrophone,
    preferredMicrophoneNames,
  ]);

  return (
    <Select
      disabled={options.length === 0}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      value={selectedId ?? undefined}
      hasError={hasError}
    >
      <SelectTrigger aria-label="Audio Source Selector">
        {selectedMicrophone?.label ?? (
          <span className="text-text-supporting">Select a microphone</span>
        )}
      </SelectTrigger>
      <SelectContent usesPortal={usesPortal} side={side}>
        {options.map((device, index) => (
          <SelectItem
            key={`${device.deviceId}-${index}`}
            className="pl-7 focus:bg-bg-accent-quaternary"
            value={device.deviceId}
          >
            {device.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
